import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { DotsSixVerticalIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import type { Skill } from "@reactive-resume/schema";

import { LEVELS } from "./suggestions";

type Props = { item: Skill; onEdit: (item: Skill) => void; onDelete: (id: string) => void };

export const SkillCard = ({ item, onEdit, onDelete }: Props) => {
  const sortable = useSortable({ id: item.id });
  const level = LEVELS.find((entry) => entry.value === item.level);
  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.55 : 1,
      }}
      className="grid grid-cols-[26px_minmax(0,1fr)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.04)]"
    >
      <button
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
        aria-label="Réorganiser"
        className="grid cursor-grab place-items-center border-r border-slate-100 text-slate-300 hover:bg-indigo-50 hover:text-indigo-500"
      >
        <DotsSixVerticalIcon size={13} weight="bold" />
      </button>
      <div className="min-w-0 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-xs font-bold text-indigo-950">{item.name}</h3>
            <p className="mt-0.5 truncate text-[9px] text-slate-500">
              {item.description || level?.label || "Niveau non précisé"}
            </p>
          </div>
          <div className="flex shrink-0">
            <button
              type="button"
              onClick={() => onEdit(item)}
              aria-label="Modifier"
              className="grid size-7 place-items-center rounded-lg text-indigo-600 hover:bg-indigo-50"
            >
              <PencilSimpleIcon size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              aria-label="Supprimer"
              className="grid size-7 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            >
              <TrashIcon size={14} />
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <span
                key={value}
                className={`h-1.5 w-5 rounded-full ${value <= item.level ? "bg-emerald-500" : "bg-slate-100"}`}
              />
            ))}
          </div>
          {item.keywords?.length > 0 && (
            <span className="truncate text-[8px] text-slate-400">
              {item.keywords.slice(0, 2).join(" · ")}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};
